import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, MonitorPlay, ImageIcon, BarChart3, LogOut, ChevronLeft, ChevronRight, UserCircle, Bookmark, DollarSign, CalendarX, Users, Receipt, UserPlus, ShoppingCart, FileText, Calculator, Settings, History, Wrench, Calendar, Tv } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/contexts/DataContext";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import NotificationCenter from "@/components/ui/NotificationCenter";

// import FloatingNotificationContainer from "@/components/ui/FloatingNotificationContainer"; // Eliminado temporalmente

import { useCompanyLogo } from "@/hooks/useCompanyLogo";

interface SidebarLinkProps {
  icon: React.ReactNode;
  title: string;
  path: string;
  isCollapsed: boolean;
  isActive: boolean;
  onClick: () => void;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({
  icon,
  title,
  path,
  isCollapsed,
  isActive,
  onClick
}) => {
  return (
    <Button 
      variant="ghost" 
      className={cn(
        "w-full text-left font-normal transition-all duration-300 overflow-hidden",
        isActive ? "bg-sidebar-accent text-sidebar-foreground font-medium" : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent",
        isCollapsed ? "justify-center px-3 py-2" : "justify-start gap-2 px-4 py-2"
      )} 
      onClick={onClick}
    >
      <span className="shrink-0">{icon}</span>
      {!isCollapsed && <span className="truncate">{title}</span>}
    </Button>
  );
};

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children
}) => {
  const {
    user,
    isAdmin,
    isSeller,
    logout,
    isMantenimiento
  } = useAuth();
  const {
    isLoading,
    refreshData
  } = useData();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const path = window.location.pathname;
  const { logoUrl } = useCompanyLogo();
  
  useEffect(() => {
    console.log('MainLayout: Componente montado');
  }, []);
  
  // Function to get user name from email
  const getUserName = () => {
    return user?.profile?.full_name || user?.email || "Usuario";
  };
  
  const routes = [
    {
      title: "Dashboard",
      path: "/dashboard",
      icon: <LayoutDashboard size={24} />,
    },

    {
      title: "Reproductores VNNOX",
      path: "/vnnox-players",
      icon: <Tv size={24} />,
      adminOnly: true,
    },

    {
      title: "Pantallas",
      path: "/screens",
      icon: <MonitorPlay size={24} />,
      adminOnly: true,
    },

    {
      title: "Mantenimiento de Pantallas",
      path: "/screen-maintenance",
      icon: <Wrench size={24} />,
      mantenimientoOnly: true,
    },

    {
      title: "Anuncios",
      path: "/ads",
      icon: <ImageIcon size={24} />,
      hideForMantenimiento: true,
    },
    {
      title: "Anuncios Vencidos",
      path: "/expired-ads",
      icon: <CalendarX size={24} />,
      hideForMantenimiento: true,
    },
    {
      title: "Reportes",
      path: "/reports",
      icon: <BarChart3 size={24} />,
      adminOnly: true,
    },

    {
      title: "Pantallas Favoritas",
      path: "/favorite-screens",
      icon: <Bookmark size={24} />,
      hideForMantenimiento: true,
    },
    {
      title: "Mis Ventas",
      path: "/my-sales",
      icon: <DollarSign size={24} />,
      hideForMantenimiento: true,
    },
    {
      title: "Empleados y comisiones",
      path: "/users",
      icon: <Users size={24} />,
      adminOnly: true,
    },
    {
      title: "Facturación",
      path: "/facturacion",
      icon: <Receipt size={24} />,
      hideForMantenimiento: true,
    },
    {
      title: "Mis Clientes",
      path: "/my-clients",
      icon: <UserPlus size={24} />,
      adminAndSellerOnly: true,
    },
    {
      title: "Orden de Compra",
      path: "/orden-compra",
      icon: <ShoppingCart size={24} />,
      hideForMantenimiento: true,
    },
    {
      title: "Nota de Crédito",
      path: "/nota-credito",
      icon: <FileText size={24} />,
      hideForMantenimiento: true,
    },
    {
      title: "Cotización",
      path: "/cotizacion",
      icon: <Calculator size={24} />,
      hideForMantenimiento: true,
    },
    {
      title: "Configuración",
      path: "/company-config",
      icon: <Settings size={24} />,
      adminOnly: true,
    },
  ];
  
  const filteredRoutes = routes.filter(route => {
    // Para todos los roles, aplicar filtros normales
    if (route.adminOnly && !isAdmin) return false;
    if (route.adminAndSellerOnly && !isAdmin && !isSeller) return false;
    if (route.hideForMantenimiento && isMantenimiento) return false;
    if (route.mantenimientoOnly && !isMantenimiento && !isAdmin) return false;
    return true;
  });
  

  
  return (
    <div className="flex h-screen overflow-hidden bg-[#f4f6f6]">
      <div className={cn("flex flex-col text-sidebar-foreground transition-all duration-300 bg-sidebar-background border-r border-sidebar-border shrink-0 relative", isCollapsed ? "w-[70px]" : "w-[250px]")}>
        
        <div className={cn("flex items-center h-16 px-4 bg-sidebar-background", isCollapsed ? "justify-center" : "justify-center")}>
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="Logo de la empresa" 
              className={cn("object-contain", isCollapsed ? "w-10 h-10" : "h-14")} 
            />
          ) : (
            <img 
              src="/lovable-uploads/gestorad-logo.png" 
              alt="GestorAD Logo" 
              className={cn("object-contain filter brightness-0 invert", isCollapsed ? "w-10 h-10" : "h-14")} 
            />
          )}
        </div>
        
        {!isCollapsed && (
          <>
            <div className="h-px bg-sidebar-border mx-4"></div>
            
            <div className="flex items-center py-3 px-4 bg-sidebar-background">
              <div className="flex flex-col">
                <span className="font-medium text-sidebar-foreground">
                  {getUserName()}
                </span>
                <span className="text-xs text-sidebar-foreground/90">
                  {user?.profile?.role === "admin" ? "Administrador" : 
                   user?.profile?.role === "contabilidad" ? "Contabilidad" : 
                   user?.profile?.role === "mantenimiento" ? "Enc Mantenimiento" : 
                   "Vendedor"}
                </span>
              </div>
            </div>
            
            <div className="h-px bg-sidebar-border mx-4"></div>
          </>
        )}

        <div className={cn("flex flex-col gap-1 p-2 flex-1 bg-sidebar-background scrollbar-hide", isCollapsed ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden")}>
          {filteredRoutes.map(route => (
            <SidebarLink 
              key={route.path} 
              icon={route.icon} 
              title={route.title} 
              path={route.path} 
              isCollapsed={isCollapsed} 
              isActive={path === route.path} 
              onClick={() => navigate(route.path)} 
            />
          ))}
        </div>
        
        {!isCollapsed && <div className="h-px bg-sidebar-border mx-4"></div>}
        
        <div className="p-2 bg-sidebar-background space-y-1">
          <Button 
            variant="ghost" 
            className={cn("w-full justify-start gap-2 text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent p-2", isCollapsed ? "px-3 justify-center" : "px-4")} 
            onClick={() => logout()}
          >
            <LogOut size={20} />
            {!isCollapsed && <span>Cerrar sesión</span>}
          </Button>
          
          <Button 
            variant="ghost" 
            className={cn("w-full justify-start gap-2 text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent p-2", isCollapsed ? "px-3 justify-center" : "px-4")} 
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            {!isCollapsed && <span>Colapsar menú</span>}
          </Button>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-6 min-w-0">
            {children}
          </div>
        </div>
      </div>
      
      
      {/* <FloatingNotificationContainer /> Eliminado temporalmente */}
    </div>
  );
};

export default MainLayout;
